import { useAuthenticator } from "@aws-amplify/ui-react";
import {
    Box,
    Button,
    Container,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Chip,
    Sheet,
} from "@mui/joy";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModalContext } from "../../context/modalContext";
import { useQuery } from "react-query";
import { fetchUserData } from "../../react-query/user";
import { generateClient } from "aws-amplify/api";
import { listRooms } from "../../graphql/queries";
import { Add, MeetingRoom, Lock, Public } from "@mui/icons-material";

type User = {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
};

type Room = {
    id: string;
    name: string;
    createdAt: string;
    user_id: string;
    allowComments: boolean;
    private: boolean;
};

const Dashboard = () => {
    const client = generateClient();
    const { setCreateRoomModalOpen } = useModalContext();
    const { needsRefetch, setNeedsRefetch } = useModalContext();
    const { authStatus } = useAuthenticator((context) => [context.authStatus]);
    const navigate = useNavigate();

    const [rooms, setRooms] = useState<Room[]>([]);
    const [userData, setUserData] = useState<User | null>(null);

    useEffect(() => {
        if (authStatus === "unauthenticated") {
            navigate("/signin");
        }
    }, [authStatus, navigate]);

    const fetchRooms = async (userId: string) => {
        const { data } = await client.graphql({
            query: listRooms,
            variables: { filter: { user_id: { eq: userId } } },
        });
        return data.listRooms.items;
    };

    const { refetch: refetchUserData, isLoading: userDataIsLoading } = useQuery("userData", fetchUserData, {
        onSuccess: (data: User) => {
            setUserData(data);
        },
    });
    const { refetch: refetchRooms, isLoading: roomsIsLoading } = useQuery(
        ["rooms", userData?.userId],
        () => fetchRooms(userData?.userId ?? ""),
        {
            enabled: !!userData?.userId,
            onSuccess: (data: Room[]) => {
                setRooms(data);
            },
            staleTime: 0,
            cacheTime: 0,
        }
    );

    useEffect(() => {
        if (needsRefetch && authStatus === "authenticated") {
            refetchUserData();
            refetchRooms();
            setNeedsRefetch(false);
        }
    }, [needsRefetch, authStatus, refetchUserData, refetchRooms, setNeedsRefetch]);

    const isLoading = userDataIsLoading || roomsIsLoading;

    return (
        <Box
            sx={{
                minHeight: "calc(100vh - var(--Header-height))",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.06) 0%, transparent 50%)"
                        : "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.08) 0%, transparent 50%)",
            }}
        >
            <Container
                sx={{
                    py: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                }}
            >
                {/* Welcome Section */}
                <Box
                    sx={{
                        textAlign: "center",
                        animation: "fadeInUp 0.5s ease-out",
                        "@keyframes fadeInUp": {
                            from: { opacity: 0, transform: "translateY(20px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                    }}
                >
                    {userDataIsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size="sm" />
                        </Box>
                    ) : (
                        <>
                            <Typography
                                level="h2"
                                sx={{
                                    fontWeight: 700,
                                    mb: 1,
                                }}
                            >
                                Welcome back, {userData?.firstName || "there"}! 👋
                            </Typography>
                            <Typography level="body-lg" textColor="text.secondary">
                                {rooms.length > 0
                                    ? "Manage your rooms or create a new one"
                                    : "Create your first room to start sharing updates"}
                            </Typography>
                        </>
                    )}
                </Box>

                {/* Create Room Card */}
                <Sheet
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: "xl",
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "center" },
                        justifyContent: "space-between",
                        gap: 2,
                        animation: "fadeInUp 0.5s ease-out 0.1s both",
                        "@keyframes fadeInUp": {
                            from: { opacity: 0, transform: "translateY(20px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        background: (theme) =>
                            theme.palette.mode === "dark"
                                ? "linear-gradient(135deg, rgba(66, 171, 155, 0.1) 0%, rgba(50, 141, 127, 0.05) 100%)"
                                : "linear-gradient(135deg, rgba(66, 171, 155, 0.08) 0%, rgba(230, 242, 239, 0.5) 100%)",
                        borderColor: "primary.200",
                    }}
                >
                    <Box>
                        <Typography level="title-lg" sx={{ fontWeight: 600 }}>
                            Create a New Room
                        </Typography>
                        <Typography level="body-md" textColor="text.secondary">
                            Start sharing your journey with friends and family
                        </Typography>
                    </Box>
                    <Button
                        size="lg"
                        startDecorator={<Add />}
                        onClick={() => setCreateRoomModalOpen(true)}
                        sx={{
                            px: 4,
                            boxShadow: "0 4px 14px rgba(66, 171, 155, 0.25)",
                        }}
                    >
                        Create Room
                    </Button>
                </Sheet>

                {/* Rooms Section */}
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : rooms && rooms.length > 0 ? (
                    <Box
                        sx={{
                            animation: "fadeInUp 0.5s ease-out 0.2s both",
                            "@keyframes fadeInUp": {
                                from: { opacity: 0, transform: "translateY(20px)" },
                                to: { opacity: 1, transform: "translateY(0)" },
                            },
                        }}
                    >
                        <Typography level="title-lg" sx={{ mb: 2, fontWeight: 600 }}>
                            Your Rooms
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: rooms.length === 1 ? "1fr" : "repeat(2, 1fr)",
                                    md: rooms.length === 1 ? "1fr" : rooms.length === 2 ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
                                },
                                gap: 2,
                            }}
                        >
                            {rooms.map((room, index) => (
                                <Card
                                    key={room.id}
                                    variant="outlined"
                                    sx={{
                                        transition: "all 0.2s ease",
                                        cursor: "pointer",
                                        animation: `fadeInUp 0.4s ease-out ${0.1 * (index + 1)}s both`,
                                        "@keyframes fadeInUp": {
                                            from: { opacity: 0, transform: "translateY(20px)" },
                                            to: { opacity: 1, transform: "translateY(0)" },
                                        },
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: "lg",
                                            borderColor: "primary.300",
                                        },
                                    }}
                                    onClick={() => navigate(`/room/${room.name}`)}
                                >
                                    <CardContent>
                                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: "lg",
                                                    bgcolor: "primary.softBg",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "primary.600",
                                                }}
                                            >
                                                <MeetingRoom />
                                            </Box>
                                            <Chip
                                                size="sm"
                                                variant="soft"
                                                color={room.private ? "warning" : "success"}
                                                startDecorator={room.private ? <Lock sx={{ fontSize: 14 }} /> : <Public sx={{ fontSize: 14 }} />}
                                            >
                                                {room.private ? "Private" : "Public"}
                                            </Chip>
                                        </Box>
                                        <Typography level="title-lg" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {room.name}
                                        </Typography>
                                        <Typography level="body-sm" textColor="text.tertiary">
                                            Created {new Date(room.createdAt).toLocaleDateString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                            })}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </Box>
                ) : (
                    /* Empty State */
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 8,
                            animation: "fadeInUp 0.5s ease-out 0.2s both",
                            "@keyframes fadeInUp": {
                                from: { opacity: 0, transform: "translateY(20px)" },
                                to: { opacity: 1, transform: "translateY(0)" },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: "50%",
                                bgcolor: "primary.softBg",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                                mb: 3,
                            }}
                        >
                            <MeetingRoom sx={{ fontSize: 40, color: "primary.500" }} />
                        </Box>
                        <Typography level="h4" sx={{ mb: 1 }}>
                            No rooms yet
                        </Typography>
                        <Typography level="body-md" textColor="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
                            Create your first room to start sharing live updates with your loved ones
                        </Typography>
                        <Button
                            size="lg"
                            startDecorator={<Add />}
                            onClick={() => setCreateRoomModalOpen(true)}
                        >
                            Create Your First Room
                        </Button>
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default Dashboard;
